import {
	CapTableRail,
	InseparableRedemptionLegRes,
	RedemptionLegFingerprintDto,
} from '@dealstack/shared/lib/generated';

// CAP-2033: a redemption of one interest also retires the paired counterpart title over the same
// serials, wherever it sits. That is derived server-side, so the only honest way to present it is to
// name the holders who lose title BEFORE the write. This builds that disclosure from the preview.

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export interface InseparableRedemptionDisclosure {
	title: string;
	intro: string;
	/** One line per counterpart leg. A single leg still reads as a list — the shape stays stable. */
	bullets: string[];
	/**
	 * Set when the resolution reached across more than one (mip, held_for) position of the holder,
	 * i.e. the user is getting more than the one position they had in mind.
	 */
	partitionWarning?: string;
}

const RAIL_KEYS = {
	[CapTableRail.BENEFICIAL]: {
		self: 'transactions.interest.beneficial',
		counterpart: 'transactions.interest.legal',
	},
	[CapTableRail.LEGAL]: {
		self: 'transactions.interest.legal',
		counterpart: 'transactions.interest.beneficial',
	},
};

/** `[101-200]`, the notation the tickets and the Ownership tab already use for a serial block. */
const formatRange = (leg: InseparableRedemptionLegRes): string => `[${leg.start}-${leg.end}]`;

/**
 * CAP-2039: a disclosed leg reduced to what the write compares against its own re-resolve. Sent with
 * the write so a register that moved in between is refused (120254) rather than silently retiring
 * counterparts the user was never shown.
 */
export const toLegFingerprint = (
	leg: InseparableRedemptionLegRes
): RedemptionLegFingerprintDto => ({
	counterpartHolderId: leg.counterpartHolderId,
	start: leg.start,
	end: leg.end,
	units: leg.units,
});

const countPartitions = (legs: InseparableRedemptionLegRes[]): number =>
	new Set(legs.map((leg) => `${leg.sourceMipId ?? ''}|${leg.sourceHeldForId ?? ''}`)).size;

export const buildInseparableRedemptionDisclosure = (
	legs: InseparableRedemptionLegRes[],
	interest: CapTableRail,
	securityName: string,
	holderNameById: (id: string) => string,
	t: TranslateFn
): InseparableRedemptionDisclosure => {
	const rails = RAIL_KEYS[interest];
	const selfRail = t(rails.self).toLowerCase();
	const counterpartRail = t(rails.counterpart).toLowerCase();
	const ranges = legs.map(formatRange).join(', ');

	const disclosure: InseparableRedemptionDisclosure = {
		title: t('transactions.inseparable_redemption.title'),
		intro: t('transactions.inseparable_redemption.intro', {
			selfRail,
			counterpartRail,
			ranges,
		}),
		bullets: legs.map((leg) =>
			t('transactions.inseparable_redemption.leg', {
				holder: holderNameById(leg.counterpartHolderId),
				units: leg.units,
				security: securityName,
				range: formatRange(leg),
			})
		),
	};

	if (countPartitions(legs) > 1) {
		disclosure.partitionWarning = t('transactions.inseparable_redemption.multi_partition');
	}

	return disclosure;
};
